import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import {
  APP_BAR_OFFSET,
  designColors,
  designFontFamily,
  designRadii,
  designShell,
  designType,
  designWeights,
  FORM_ACTION_BUTTON_WIDTH,
} from "services/constants/designTokens";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";

const FALLBACK_CONTENT_LEFT = 240;

export const measureContentLeft = () => {
  if (typeof document === "undefined") return FALLBACK_CONTENT_LEFT;
  const mainContent = document.querySelector(".main-content");
  const left = mainContent?.getBoundingClientRect().left;
  return left > 0 ? left : FALLBACK_CONTENT_LEFT;
};

export const useExpandedContentLeft = (expanded) => {
  const [contentLeft, setContentLeft] = useState(measureContentLeft);

  useEffect(() => {
    if (!expanded) return undefined;
    const updateContentLeft = () => setContentLeft(measureContentLeft());
    updateContentLeft();
    window.addEventListener("resize", updateContentLeft);
    return () => window.removeEventListener("resize", updateContentLeft);
  }, [expanded]);

  return contentLeft;
};

export const globalTextFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: designRadii.control,
    backgroundColor: designColors.white,
  },
};

// `minHeight` is opt-in — used by screens that lay paired fields out
// side-by-side in a 2-column grid (AddCompany, CompanyDetailPanel's inline
// edit) so both inputs in a row start at the same Y position regardless of
// which sibling's tooltip wraps to more lines. Reserved on the label block
// itself rather than stretching+bottom-aligning the whole field: a
// validation error appends text *below* the input, and stretch-alignment
// would let that push the *sibling's* input down out of alignment too.
// Fixed height on the pre-input block is immune to that.
export const PAIRED_LABEL_MIN_HEIGHT = 64;

export const GlobalFieldLabel = ({ children, helperText, minHeight }) => (
  <Mui.Box sx={{ mb: 0.75, ...(minHeight ? { minHeight } : {}) }}>
    <Mui.Typography
      sx={{
        fontSize: 14,
        fontWeight: 500,
        color: designColors.slate900,
        lineHeight: 1.35,
      }}
    >
      {children}
    </Mui.Typography>
    {helperText ? (
      <Mui.Typography
        sx={{
          mt: 0.4,
          fontSize: 12.5,
          fontWeight: 400,
          color: designColors.slate600,
          lineHeight: 1.45,
        }}
      >
        {helperText}
      </Mui.Typography>
    ) : null}
  </Mui.Box>
);

// Trail shown atop the expanded/full-page forms in place of the old single
// "‹ Back" icon. Each entry is either a plain string (inert — used for
// "Administration" and the current screen, which is always last) or
// { label, onClick } for a segment that should navigate away, e.g. the list
// this form was opened from — same destination the old back icon went to.
// The last segment is never clickable even if given an onClick, since it
// names the screen you're already on.
export const GlobalBreadcrumb = ({ trail = [], sx = {} }) => (
  <Mui.Breadcrumbs
    separator="›"
    sx={{
      mb: 0.75,
      flexWrap: "wrap",
      overflow: "visible",
      // Same size and line height as the crumbs themselves: with the default
      // 16px line, clickable crumbs (inline buttons) sat lower than the
      // plain ones next to them.
      fontSize: 12.5,
      lineHeight: 1.5,
      "& .MuiBreadcrumbs-ol": {
        flexWrap: "wrap",
        alignItems: "center",
      },
      "& .MuiBreadcrumbs-li": { display: "flex", alignItems: "center" },
      "& .MuiBreadcrumbs-separator": { color: designColors.slate400, mx: 0.6 },
      ...sx,
    }}
  >
    {trail.map((item, index) => {
      const isLast = index === trail.length - 1;
      const label = typeof item === "string" ? item : item.label;
      const onClick = typeof item === "string" ? undefined : item.onClick;

      if (onClick && !isLast) {
        return (
          <Mui.Link
            key={label}
            component="button"
            type="button"
            underline="hover"
            onClick={onClick}
            sx={{
              // Reset the native <button> chrome (padding/border/background)
              // that `component="button"` otherwise leaves in place — without
              // this, the browser's default button padding/line-height shifts
              // these crumbs off the baseline of their plain-text siblings.
              p: 0,
              m: 0,
              border: 0,
              background: "none",
              display: "block",
              lineHeight: 1.5,
              fontFamily: "inherit",
              cursor: "pointer",
              fontSize: 12.5,
              fontWeight: 500,
              color: designColors.slate500,
              whiteSpace: "normal",
              wordBreak: "break-word",
              "&:hover": { color: designColors.tealDark },
            }}
          >
            {label}
          </Mui.Link>
        );
      }

      return (
        <Mui.Typography
          key={label}
          sx={{
            fontSize: 12.5,
            lineHeight: 1.5,
            fontWeight: isLast ? 700 : 500,
            color: isLast ? designColors.tealDark : designColors.slate500,
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {label}
        </Mui.Typography>
      );
    })}
  </Mui.Breadcrumbs>
);

export const GlobalNoticeBanner = ({ icon, children, sx = {} }) => (
  <Mui.Stack
    direction="row"
    spacing={1.5}
    alignItems="flex-start"
    sx={{
      backgroundColor: designColors.tealTint,
      border: "1px solid #B7E4DB",
      borderRadius: designRadii.banner,
      p: 2,
      ...sx,
    }}
  >
    {icon ? (
      <Mui.Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: designRadii.iconButton,
          backgroundColor: designColors.white,
          color: designColors.tealDark,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Mui.Box>
    ) : null}
    <Mui.Typography
      sx={{ fontSize: 13.5, color: designColors.slate700, pt: 0.5 }}
    >
      {children}
    </Mui.Typography>
  </Mui.Stack>
);

export const GlobalDetailSectionCard = ({
  title,
  action,
  children,
  titleSx = {},
}) => (
  <Mui.Box
    sx={{
      border: `1px solid ${designColors.slate200}`,
      borderRadius: designRadii.card,
      backgroundColor: designColors.white,
      overflow: "hidden",
    }}
  >
    <Mui.Box
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${designColors.slate200}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <Mui.Typography sx={{ m: 0, ...titleSx }}>{title}</Mui.Typography>
      {action}
    </Mui.Box>
    <Mui.Box sx={{ px: 2, py: 1.75 }}>{children}</Mui.Box>
  </Mui.Box>
);

// One confirm/cancel popup for the teal/slate design system, replacing the
// old app-wide GlobalDialogBox (mint-green CustomButton pair, unstyled
// plain-text title) wherever a screen has already opted into this redesign.
// Same reasoning as PrimaryButton/SecondaryButton in DesignSystemButtons.js:
// a single shared implementation so the look can't drift out of sync between
// screens the way the old per-screen copies did (Users vs Companies each
// hand-rolled their own Activate/Deactivate confirm popup).
export const GlobalConfirmDialog = ({
  open,
  title,
  children,
  onCancel,
  onConfirm,
  confirmLabel = "Yes",
  cancelLabel = "No",
  loading = false,
  // Was hard-coded to "Rejecting..." below regardless of caller — fine
  // while this dialog only ever backed a Reject action, but every other
  // "danger" caller (e.g. a Cancel confirmation) would show that same
  // mismatched label while its own action is in flight. Defaults to the
  // original string so every existing caller is unaffected; a new danger
  // caller for a different action should pass its own.
  loadingLabel = "Rejecting...",
  confirmDisabled = false,
  confirmStartIcon,
  danger = false,
  reasonValue,
  onReasonChange,
  reasonError = "",
  reasonPlaceholder = "Enter a reason for rejection",
  reasonMinRows = 3,
  contentSx,
  maxWidth = "xs",
}) => (
  <Mui.Dialog
    open={open}
    onClose={onCancel}
    maxWidth={maxWidth}
    fullWidth
    PaperProps={{
      sx: { borderRadius: designRadii.card },
    }}
  >
    <Mui.DialogTitle
      sx={{ fontSize: 18, fontWeight: 700, color: designColors.slate900 }}
    >
      {title}
    </Mui.DialogTitle>
    <Mui.DialogContent>
      <Mui.Box
        sx={{ fontSize: 14.5, color: designColors.slate700, ...contentSx }}
      >
      {children}
      {onReasonChange ? (
        <Mui.TextField
          multiline
          minRows={reasonMinRows}
          fullWidth
          autoFocus
          placeholder={reasonPlaceholder}
          value={reasonValue}
          onChange={(event) => onReasonChange(event.target.value)}
          error={Boolean(reasonError)}
          helperText={reasonError}
          sx={{
            ...globalTextFieldSx,
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: designColors.teal,
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: designColors.teal,
            },
          }}
        />
      ) : null}
      </Mui.Box>
    </Mui.DialogContent>
    <Mui.DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
      <SecondaryButton onClick={onCancel}>{cancelLabel}</SecondaryButton>
      {danger ? (
        <Mui.Button
          variant="outlined"
          onClick={onConfirm}
          disabled={loading || confirmDisabled}
          startIcon={confirmStartIcon}
          sx={{
            minHeight: 46,
            minWidth: FORM_ACTION_BUTTON_WIDTH,
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: "#FEF2F2",
            borderColor: "#FECACA",
            color: "#DC2626",
            "&:hover": {
              backgroundColor: "#FEE2E2",
              borderColor: "#FCA5A5",
            },
          }}
        >
          {loading ? loadingLabel : confirmLabel}
        </Mui.Button>
      ) : (
        <PrimaryButton
          onClick={onConfirm}
          loading={loading}
          disabled={confirmDisabled}
        >
          {confirmLabel}
        </PrimaryButton>
      )}
    </Mui.DialogActions>
  </Mui.Dialog>
);

// Shared "list page" chrome tokens — first factored out of
// TeamZone/Approve/approveUi.js so any other module's list screen can reuse
// the same page surface/card/sticky-bar/table-cell look instead of
// redefining it locally, the way Approve's own sub-screens already share
// these from approveUi.js.
export const sharedListPageSurfaceSx = {
  ...designShell.pageSurface,
  py: { xs: 1.5, md: 1.25 },
  px: { xs: 1.5, md: 1.5 },
};

export const sharedListCardSx = {
  ...designShell.sectionCard,
  p: 2,
};

// Sticks directly under the fixed AppBar (APP_BAR_OFFSET) — same "tuck
// under the AppBar" convention as the panel drawers use.
export const sharedStickyCustomFilterBarSx = {
  position: { xs: "static", md: "sticky" },
  top: { md: APP_BAR_OFFSET },
  zIndex: 4,
  backgroundColor: designColors.white,
};

export const sharedStickyFooterBarSx = {
  position: "sticky",
  bottom: -1,
  zIndex: 3,
  px: 2,
  py: 0.75,
  borderTop: `1px solid ${designColors.slate200}`,
  backgroundColor: designColors.white,
  boxShadow: "0 -10px 24px rgba(15, 23, 42, 0.06)",
};

export const sharedTableHeadCellSx = {
  ...designShell.tableHeadCellBase,
  py: 0.75,
  position: "sticky",
  top: APP_BAR_OFFSET,
  zIndex: 2,
};

export const sharedTableBodyCellSx = {
  py: 0.75,
  fontSize: 14,
  color: designColors.slate900,
  borderBottom: `1px solid ${designColors.slate200}`,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

// Detail-panel text tokens, ported byte-for-byte from Administration's
// design system (designType.panelTitle/helperText) plus two additions
// (fieldLabel/fieldValue) for the label/value pairs Approve's detail
// panels need that Administration's own tokens don't cover.
export const sharedPanelTextUi = {
  panelTitle: {
    ...designType.panelTitle,
    fontFamily: designFontFamily,
  },
  panelSectionTitle: {
    fontSize: 15,
    fontWeight: designWeights.heading,
    color: designColors.slate900,
    fontFamily: designFontFamily,
  },
  panelMutedText: {
    ...designType.helperText,
    fontFamily: designFontFamily,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: designWeights.semibold,
    color: designColors.slate600,
    fontFamily: designFontFamily,
  },
  fieldValue: {
    fontSize: 14,
    fontWeight: designWeights.semibold,
    color: designColors.slate900,
    fontFamily: designFontFamily,
  },
};
